/**
 * ChartOfAccountsTree — Reusable tree component for Financial Accounting screens
 * Shows: Primary Groups (green) → Group Accounts (blue) → General Ledgers (orange)
 * Hierarchy: Balance Sheet (Assets, Liabilities) + Profit & Loss (Incomes, Expenses)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronDown, Search, RefreshCw } from 'lucide-react';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';


const NODE_COLORS = {
  root:    { bg: '#d32f2f', text: '#fff' },
  primary: { bg: '#2e7d32', text: '#fff', label: 'Primary Groups' },
  group:   { bg: '#1565c0', text: '#fff', label: 'Group Accounts' },
  general: { bg: '#e65100', text: '#fff', label: 'General Ledgers' },
};

const TreeNodeIcon = ({ type, size = 14 }) => {
  const c = NODE_COLORS[type] || NODE_COLORS.primary;
  return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: 2, background: c.bg, flexShrink: 0 }} />;
};

const TreeNode = ({ label, code, type, children, level = 0, onSelect, selectedId, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded || level < 1);
  const hasChildren = children && children.length > 0;
  const isSelected = selectedId === code;
  const indent = level * 16;
  const colors = {
    root: '#d32f2f', primary: '#2e7d32', group: '#1565c0', general: '#e65100'
  };
  const textColor = colors[type] || '#333';

  return (
    <div>
      <div
        data-testid={`tree-node-${code}`}
        onClick={() => { if (hasChildren) setExpanded(!expanded); if (onSelect) onSelect({ code, label, type }); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, paddingLeft: indent + 4, paddingRight: 6,
          paddingTop: 3, paddingBottom: 3, cursor: 'pointer', fontSize: 12, fontWeight: type === 'root' ? 700 : 500,
          color: textColor, background: isSelected ? 'var(--erp-hover)' : 'transparent',
          borderLeft: isSelected ? '2px solid var(--erp-primary)' : '2px solid transparent',
          transition: 'background 0.1s'
        }}
        onMouseEnter={(e) => { if (!e.currentTarget || isSelected) return; e.currentTarget.style.background = 'var(--erp-hover)'; }}
        onMouseLeave={(e) => { if (!e.currentTarget || isSelected) return; e.currentTarget.style.background = 'transparent'; }}
      >
        {hasChildren ? (
          expanded ? <ChevronDown size={13} style={{ flexShrink: 0 }} /> : <ChevronRight size={13} style={{ flexShrink: 0 }} />
        ) : <span style={{ width: 13, flexShrink: 0 }} />}
        <TreeNodeIcon type={type} size={12} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      {expanded && hasChildren && (
        <div>{children.map((child, i) => <TreeNode key={child.code || i} {...child} level={level + 1} onSelect={onSelect} selectedId={selectedId} />)}</div>
      )}
    </div>
  );
};

const ChartOfAccountsTree = ({ onSelect, selectedId, highlightType, refreshKey }) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const token = getToken() || '';

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success) {
        const { primaryGroups = [], groupLedgers = [], generalLedgers = [] } = j.data || {};
        // Build tree: Balance Sheet + Profit & Loss
        const buildSubTree = (pgList) => pgList.map(pg => ({
          code: pg.PGroupId, label: pg.PGroupName, type: 'primary',
          children: groupLedgers.filter(gl => gl.PrimaryGroup === pg.PGroupId).map(gl => ({
            code: gl.GroupId, label: gl.GroupName, type: 'group',
            children: generalLedgers.filter(gen => gen.GroupId === gl.GroupId).map(gen => ({
              code: gen.GLId, label: gen.GLName, type: 'general', children: []
            }))
          }))
        }));
        const bsPrimary = primaryGroups.filter(pg => pg.Category === 'BS');
        const plPrimary = primaryGroups.filter(pg => pg.Category === 'PL');
        // Separate Assets/Liabilities under BS
        const assets = bsPrimary.filter(pg => pg.PGroupType === '01' || pg.AccountType === 'Assets');
        const liabilities = bsPrimary.filter(pg => pg.PGroupType === '02' || pg.AccountType === 'Liabilities');
        const incomes = plPrimary.filter(pg => pg.PGroupType === '03' || pg.AccountType === 'Incomes');
        const expenses = plPrimary.filter(pg => pg.PGroupType === '04' || pg.AccountType === 'Expenses');

        const tree = [
          { code: 'BS', label: 'Balance Sheet', type: 'root', defaultExpanded: true, children: [
            { code: 'BS-A', label: 'Assets', type: 'group', defaultExpanded: true, children: buildSubTree(assets.length ? assets : bsPrimary.slice(0, Math.ceil(bsPrimary.length / 2))) },
            { code: 'BS-L', label: 'Liabilities', type: 'group', defaultExpanded: true, children: buildSubTree(liabilities.length ? liabilities : bsPrimary.slice(Math.ceil(bsPrimary.length / 2))) },
          ]},
          { code: 'PL', label: 'Profit and Loss', type: 'root', defaultExpanded: true, children: [
            { code: 'PL-I', label: 'Incomes', type: 'group', defaultExpanded: true, children: buildSubTree(incomes.length ? incomes : plPrimary.slice(0, Math.ceil(plPrimary.length / 2))) },
            { code: 'PL-E', label: 'Expenses', type: 'group', defaultExpanded: true, children: buildSubTree(expenses.length ? expenses : plPrimary.slice(Math.ceil(plPrimary.length / 2))) },
          ]},
        ];
        setTreeData(tree);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [token, refreshKey]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // Filter tree by search
  const filterTree = (nodes, term) => {
    if (!term) return nodes;
    return nodes.map(n => {
      const match = n.label?.toLowerCase().includes(term) || n.code?.toLowerCase().includes(term);
      const filteredChildren = n.children ? filterTree(n.children, term) : [];
      if (match || filteredChildren.length > 0) return { ...n, children: filteredChildren, defaultExpanded: true };
      return null;
    }).filter(Boolean);
  };
  const displayed = search ? filterTree(treeData, search.toLowerCase()) : treeData;

  return (
    <div data-testid="chart-of-accounts-tree" style={{ display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid var(--erp-border)', background: 'var(--erp-bg)' }}>
      {/* Header */}
      <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--erp-border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, flex: 1 }}>Chart of Accounts</span>
        <button onClick={loadTree} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><RefreshCw size={12} /></button>
      </div>
      {/* Search */}
      <div style={{ padding: '4px 6px', borderBottom: '1px solid var(--erp-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--erp-input-bg)', border: '1px solid var(--erp-border)', borderRadius: 4, padding: '2px 6px' }}>
          <Search size={11} style={{ opacity: 0.5 }} />
          <input data-testid="tree-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 11, width: '100%', padding: '2px 0', color: 'inherit' }} />
        </div>
      </div>
      {/* Tree */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--erp-text-muted)' }}>Loading...</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--erp-text-muted)' }}>No accounts found</div>
        ) : (
          displayed.map((n, i) => <TreeNode key={n.code || i} {...n} onSelect={onSelect} selectedId={selectedId} />)
        )}
      </div>
      {/* Legend */}
      <div style={{ padding: '6px 8px', borderTop: '1px solid var(--erp-border)', display: 'flex', gap: 12, fontSize: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><TreeNodeIcon type="primary" size={10} /> Primary Groups</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><TreeNodeIcon type="group" size={10} /> Group Accounts</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><TreeNodeIcon type="general" size={10} /> General Ledgers</span>
      </div>
    </div>
  );
};

export default ChartOfAccountsTree;
